import {
  Badge,
  ErrorBoundary,
  FlexBox,
  Modal,
  ModalBody,
  ModalHead,
} from '@dixa/lib-shared-habla';
import type {
  InstrumentPanel,
  ReadInstrumentsQueryFn,
} from 'lib-analytics-types';
import { isResponseWithData } from 'lib-shared-auth';
import { useCallback, useEffect } from 'react';

import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { useAnalyticsState } from '../../../../utils/useAnalyticsState/useAnalyticsState';
import { usePersistInstrumentSettings } from '../../utils/usePersistInstrumentSettings';
import { InstrumentPanelTitle } from '../panel/components/title/instrumentPanelTitle';
import { InstrumentRendererError } from '../renderer/components/error/instrumentRendererError';
import { InstrumentRenderer } from '../renderer/instrumentRenderer';

import styles from './instrumentModal.module.scss';

type InstrumentModalProps = {
  /** The instrument panel. */
  panel: InstrumentPanel;
  /** Called when the modal is closed. */
  onClose: () => void;
};

export const InstrumentModal = ({
  panel,
  onClose,
}: InstrumentModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { context, period, filters, settings } = useAnalyticsState();

  const { instrumentSettings, setInstrumentSettings } =
    usePersistInstrumentSettings();

  const [readInstrumentsQuery, { instruments, isFetching, isError }] =
    analyticsApi.useLazyReadInstrumentsQuery({
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        instruments: data?.data.instruments ?? [],
      }),
    });

  const readInstruments: ReadInstrumentsQueryFn = useCallback(
    async (arg) => {
      const response = await readInstrumentsQuery({
        id: panel.id,
        context,
        period,
        filters,
        settings: { ...settings, ...instrumentSettings },
        meta: {
          ...arg?.meta,
          in_depth: true,
        },
      });

      if (isResponseWithData(response)) {
        return response.data;
      }
    },
    [
      context,
      filters,
      instrumentSettings,
      panel.id,
      period,
      readInstrumentsQuery,
      settings,
    ],
  );

  /**
   * Fetches the in-depth instrument data:
   *
   * - On initial component mount.
   * -
   * - When the `instrumentSettings` change. This happens when the user changes a
   *   setting specific to an instrument, such as chart granularity.
   */
  useEffect(() => {
    readInstruments();

    // Only run this effect when the relevant data points that should trigger an API request change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentSettings]);

  const handleExport = (): void => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'analytics:export',
          arg: {
            instruments,
            panel,
          },
        },
      }),
    );
  };

  return (
    <Modal className={styles.modal} isOpen onClose={onClose}>
      <ModalHead
        title={
          <FlexBox gap="200" data-analytics-panel={panel.id}>
            <Badge variant="primary" size="medium">
              In-depth
            </Badge>

            <InstrumentPanelTitle panel={panel} />
          </FlexBox>
        }
        secondaryButton={
          panel.exportable
            ? {
                icon: 'download',
                tooltipContent: 'Export',
                onClick: handleExport,
              }
            : undefined
        }
        onClose={onClose}
      />

      <ModalBody className={styles.modalBody}>
        <ErrorBoundary fallback={<InstrumentRendererError />}>
          <InstrumentRenderer
            panel={panel}
            instruments={instruments}
            isFetching={isFetching}
            isError={isError}
            onReadInstruments={readInstruments}
            onSetInstrumentSettings={setInstrumentSettings}
          />
        </ErrorBoundary>
      </ModalBody>
    </Modal>
  );
};
